import { useState } from "react";
import type { FormEvent } from "react";

interface BreakGlassFormProps {
  onSubmit: (username: string, password: string) => Promise<void>;
  busy: boolean;
  error: string | null;
}

/**
 * The local account sign-in, for when the identity provider is unreachable or not yet configured.
 *
 * Collapsed behind a disclosure and styled as secondary on purpose: it is a recovery path, not the
 * way in. An operator who reaches for it should feel they are doing something unusual, because they
 * are — this account is invisible to directory offboarding and to organisational MFA.
 */
export function BreakGlassForm({ onSubmit, busy, error }: BreakGlassFormProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (username.trim() && password) {
      void onSubmit(username.trim(), password);
    }
  };

  if (!open) {
    return (
      <button type="button" className="link-button" onClick={() => setOpen(true)}>
        Sign in with the local administrator account
      </button>
    );
  }

  return (
    <form className="break-glass-form" onSubmit={handleSubmit}>
      <p className="form-note">
        This account does not use your organisation&rsquo;s sign-in. It can administer this
        deployment but cannot read any indexed file.
      </p>
      <label htmlFor="break-glass-username">Username</label>
      <input
        id="break-glass-username"
        type="text"
        autoComplete="username"
        value={username}
        onChange={(event) => setUsername(event.target.value)}
        disabled={busy}
        required
      />
      <label htmlFor="break-glass-password">Password</label>
      <input
        id="break-glass-password"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        disabled={busy}
        required
      />
      {error && <p className="field-error">{error}</p>}
      <button
        type="submit"
        className="button button--secondary"
        disabled={busy || !username.trim() || !password}
      >
        {busy ? "Checking…" : "Sign in"}
      </button>
    </form>
  );
}
